import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { DropdownModule, DropdownMenuData, DropdownMenuItem } from '@vg-constellation/angular-17/dropdown';
import { InputComponent } from '@vg-constellation/angular-17/input';
import { LabelComponent } from '@vg-constellation/angular-17/label';
import { IconModule } from '@vg-constellation/angular-17/icon';
import { IconButtonComponent } from '@vg-constellation/angular-17/button';
import { LinkComponent } from '@vg-constellation/angular-17/link';
import { CardComponent } from '@vg-constellation/angular-17/card';

import { BeneficiarySelection, BeneficiarySectionType, DropdownStateChangeEvent, getCardDensity } from '@models/beneficiary.model';
import { WebLogger } from '@weps/shared-http';
import * as content from '@services/content/guided-flow/account-details-content';

@Component({
  selector: 'app-beneficiary-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    InputComponent,
    LabelComponent,
    IconModule,
    IconButtonComponent,
    LinkComponent,
    CardComponent,
  ],
  templateUrl: './beneficiary-card.component.html',
  styleUrls: ['./beneficiary-card.component.scss'],
})
export class BeneficiaryCardComponent implements OnInit {
  @Input() beneficiary!: BeneficiarySelection;
  @Input() section: BeneficiarySectionType = 'PRIMARY';
  @Input() householdMembers: DropdownMenuItem[] = [];

  @Output() edit = new EventEmitter<BeneficiarySelection>();
  @Output() remove = new EventEmitter<BeneficiarySelection>();
  @Output() distributionChange = new EventEmitter<{ beneficiary: BeneficiarySelection; value: number }>();
  @Output() personChange = new EventEmitter<{ beneficiary: BeneficiarySelection; memberId: string }>();

  protected readonly content = content;

  personDropdownData: DropdownMenuData = { groups: [] };
  activePersonId = '';
  cardDensity: 'small' | 'medium' = 'medium';

  get displayName(): string {
    return `${this.beneficiary.firstName} ${this.beneficiary.lastName}`;
  }

  ngOnInit(): void {
    this.buildPersonDropdown();
    this.cardDensity = getCardDensity();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.cardDensity = getCardDensity();
  }

  private buildPersonDropdown(): void {
    const currentPersonItem: DropdownMenuItem = {
      labelText: this.displayName,
      id: this.beneficiary.householdMemberId || 'current-person',
    };

    const otherMembers = this.householdMembers.filter(
      m => m.id !== this.beneficiary.householdMemberId
    );

    this.personDropdownData = {
      groups: [{ items: [currentPersonItem, ...otherMembers] }],
    };

    this.activePersonId = currentPersonItem.id?.toString() || '';
  }

  handlePersonDropdownChange(event: DropdownStateChangeEvent): void {
    const selectedId = event?.activeItems?.[0];
    if (selectedId && selectedId !== this.activePersonId) {
      WebLogger.log({
        message: 'BeneficiaryCard person changed',
        payload: {
          event_type: 'beneficiary_card_person_changed',
          previous_member_id: this.activePersonId,
          new_member_id: selectedId,
        },
      });
      this.activePersonId = selectedId;
      this.personChange.emit({ beneficiary: this.beneficiary, memberId: selectedId });
    }
  }

  handleEditClick(): void {
    this.edit.emit(this.beneficiary);
  }

  handleRemoveClick(): void {
    this.remove.emit(this.beneficiary);
  }

  handleDistributionInput(input: HTMLInputElement): void {
    // Allow only numeric characters (strip everything else)
    const cursorPosition = input.selectionStart || 0;
    const originalValue = input.value;
    const cleanedValue = originalValue.replace(/[^0-9]/g, '');

    if (originalValue !== cleanedValue) {
      input.value = cleanedValue;
      // Adjust cursor position based on how many characters were removed before cursor
      const textBeforeCursor = originalValue.substring(0, cursorPosition);
      const cleanedBeforeCursor = textBeforeCursor.replace(/[^0-9]/g, '');
      const newPosition = cleanedBeforeCursor.length;
      input.setSelectionRange(newPosition, newPosition);
    }
  }

  handleDistributionFocus(input: HTMLInputElement): void {
    // Remove the '%' sign and place cursor at end after Angular/c11n rendering completes
    requestAnimationFrame(() => {
      const currentValue = input.value.replace('%', '').trim();
      input.value = currentValue;
      const len = currentValue.length;
      input.setSelectionRange(len, len);
    });
  }

  handleDistributionBlur(input: HTMLInputElement): void {
    const value = input.value;

    // Strip '%' and whitespace, then parse as integer (MEZ API spec: integer only)
    const cleanedValue = value.replace(/[%\s]/g, '').trim();

    let numValue = parseInt(cleanedValue, 10);

    // Handle invalid input
    if (isNaN(numValue)) {
      numValue = 0;
    }

    // Clamp to [0, 100]; 0 is allowed during data entry, distribution validation catches it on submit
    numValue = Math.max(0, Math.min(100, numValue));

    if (cleanedValue !== String(numValue)) {
      WebLogger.log({
        message: 'BeneficiaryCard distribution value sanitized',
        level: 'warn',
        payload: {
          event_type: 'beneficiary_card_distribution_sanitized',
          raw_value: cleanedValue,
          sanitized_value: numValue,
          beneficiary_id: this.beneficiary.householdMemberId,
        },
      });
    }

    // Update the input display with the '%' sign
    input.value = numValue + '%';

    this.distributionChange.emit({ beneficiary: this.beneficiary, value: numValue });
  }
}
